import {useFluxStore} from 'client/framework';
import {debounce} from 'lodash';
import moment from 'moment';
import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {Button, Heading, Icon, IconButton, SearchInput, SegmentedControl} from '@albert-io/atomic';
import {ErrorRetryState} from 'client/TeacherDashboard/components/ErrorRetryState';
import {SortDropdown} from 'client/TeacherDashboard/components/SortDropdown';
import type {
  AssignmentTypeFilter,
  ClassworkCreatedByFilter,
  SelectOption,
  SortValue
} from 'client/TeacherDashboard/types';
import {
  useClassContentRevealed,
  useReportClassContentReady
} from 'client/TeacherDashboard/ClassDetail/useClassContentReady';
import masqueradeStore from 'client/generic/Masquerade/Masquerade.store';
import {history} from 'client/history';

import {AssignmentRow} from './AssignmentRow';
import {
  AssignmentsFilteredEmptyState,
  AssignmentsOnboardingEmptyState,
  AssignmentStatusEmptyState
} from './AssignmentsEmptyState';
import {getAssignmentsScope} from './AssignmentsList.helpers';
import type {StatusSegment} from './constants';
import {getCreateAssignmentPath, STATUS_SEGMENTS} from './constants';
import {FiltersMenu} from './FiltersMenu';
import {useAssignmentStatusCounts} from './hooks/useAssignmentStatusCounts';
import type {AssignmentSortValue} from './hooks/useTeacherAssignments';
import {useTeacherAssignments} from './hooks/useTeacherAssignments';
import {AssignmentRowsSkeleton, AssignmentsViewSkeleton} from './AssignmentsSkeleton';
import {AssignmentLtiSyncCell, CrossClassLtiSyncCell} from './lmsSync/AssignmentLtiSyncCell';
import {useAnyClassLtiDeployment, useClassLtiSync} from './lmsSync/useClassLtiSync';
import type {TeacherAssignmentRowFieldsFragment} from './queries/TeacherAssignments.queries.generated';

const SORT_OPTIONS: ReadonlyArray<SelectOption<AssignmentSortValue>> = [
  {value: 'recentlyEdited', label: 'Recently Edited'},
  {value: 'name', label: 'Name'},
  {value: 'dueDate', label: 'Due Date'}
];

const DEFAULT_SORT: SortValue<AssignmentSortValue> = {field: 'recentlyEdited', order: 'desc'};

const getInitialSegment = (): StatusSegment => {
  const hash = window.location.hash.substring(1);
  // The legacy list linked here with #drafts.
  if (hash === 'drafts') {
    return 'draft';
  }
  const match = STATUS_SEGMENTS.find((s) => s.value === hash);
  return match ? match.value : 'open';
};

interface Props {
  /** Scopes the list to one class (the class detail page); omitted for the cross-class view. */
  classroomId?: string;
}

export const AssignmentsList = ({classroomId}: Props) => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');
  const [isSearchOpen, setIsSearchOpen] = useState<boolean>(false);
  const [createdBy, setCreatedBy] = useState<ClassworkCreatedByFilter>('all');
  const [assignmentType, setAssignmentType] = useState<AssignmentTypeFilter>('all');
  // Due-date range bounds as YYYY-MM-DD (native date-input format); '' = unset.
  const [dueDateStart, setDueDateStart] = useState('');
  const [dueDateEnd, setDueDateEnd] = useState('');
  const [sort, setSort] = useState<SortValue<AssignmentSortValue>>(DEFAULT_SORT);
  const [segment, setSegment] = useState<StatusSegment>(getInitialSegment);

  // Defined before the scope-switch reset below so that reset can cancel a pending debounce.
  const debouncedSetSearch = useMemo(() => debounce(setDebouncedSearchTerm, 200), []);
  useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch]);

  const isDraftSegment = segment === 'draft';

  // The shell holds the header + this view behind one skeleton until both the banner and the content
  // below are ready, then flips them together.
  const revealed = useClassContentRevealed();

  // Switching class scope (via the sidemenu dropdown) keeps you on the assignments tab but resets
  // filters + sort.
  const [trackedClassroomId, setTrackedClassroomId] = useState(classroomId);
  if (trackedClassroomId !== classroomId) {
    setTrackedClassroomId(classroomId);
    setSearchTerm('');
    debouncedSetSearch.cancel();
    setDebouncedSearchTerm('');
    setCreatedBy('all');
    setAssignmentType('all');
    setDueDateStart('');
    setDueDateEnd('');
    setSort(DEFAULT_SORT);
  }

  const actorTeacherId = useFluxStore(masqueradeStore, (s) => s.getUserIdByMasqueradeState() ?? '');

  const isMasquerading = useFluxStore(masqueradeStore, (s) => s.isMasquerading());
  const asEducatorId = isMasquerading ? actorTeacherId : undefined;
  // Local-day instants, so the filter agrees with the local-time due dates the rows display
  // (a UTC day boundary would drop end-of-day assignments for any non-UTC teacher).
  const dueDateStartIso = dueDateStart ? moment(dueDateStart).startOf('day').toISOString() : null;
  const dueDateEndIso = dueDateEnd ? moment(dueDateEnd).endOf('day').toISOString() : null;

  const {assignments, isLoading, hasError, hasNextPage, isLoadingMore, loadMore, refetch} =
    useTeacherAssignments({
      search: debouncedSearchTerm,
      segment,
      createdBy,
      assignmentType,
      dueDateStart: dueDateStartIso,
      dueDateEnd: dueDateEndIso,
      actorTeacherId,
      asEducatorId,
      sort,
      classroomId
    });

  // Counts track the active filters, so each tab label predicts exactly what clicking it shows.
  // They double as the scope-wide existence check for getAssignmentsScope below.
  const {
    counts,
    isLoading: isCountsLoading,
    hasError: hasCountsError,
    refetchCounts
  } = useAssignmentStatusCounts({
    search: debouncedSearchTerm,
    createdBy,
    assignmentType,
    dueDateStart: dueDateStartIso,
    dueDateEnd: dueDateEndIso,
    actorTeacherId,
    asEducatorId,
    classroomId
  });

  // A retry or delete changes the totals too — refresh the list and the selector counts together.
  // Stable ref: it feeds the React.memo'd AssignmentRow, which the old bare `refetch` kept intact.
  const refreshListAndCounts = useCallback(() => {
    refetch();
    refetchCounts();
  }, [refetch, refetchCounts]);

  const segmentOptions = useMemo(
    () =>
      STATUS_SEGMENTS.map((s) => ({
        ...s,
        label: counts ? (
          <>
            {s.label} <span className='ml-x0-half content-tertiary'>({counts[s.value]})</span>
          </>
        ) : (
          s.label
        )
      })),
    [counts]
  );

  const handleSegmentChange = (value: string | number) => {
    if (value === segment) {
      return;
    }
    window.location.hash = String(value);
    setSegment(value as StatusSegment);
  };

  const clearSearch = () => {
    setSearchTerm('');
    debouncedSetSearch.cancel();
    setDebouncedSearchTerm('');
  };

  // Drafts ignore the creator/type/date selections (the menu is hidden there, the query skips
  // them), so only search counts as an active filter on that tab.
  const hasFiltersOn =
    !!debouncedSearchTerm ||
    (!isDraftSegment &&
      (createdBy !== 'all' || assignmentType !== 'all' || !!dueDateStart || !!dueDateEnd));
  const {isCheckingScope, isScopeEmpty} = getAssignmentsScope({
    assignmentCount: assignments.length,
    hasFiltersOn,
    isListLoading: isLoading,
    counts,
    isCountsLoading,
    hasCountsError
  });

  // Report once the list + counts have both settled; the shell reveals the header + this list
  // together on it, so tabs, counts, and rows appear at once (no "Status (0)" or tabs flashing before
  // the "No assignments" state). A counts error still reports (labels just drop their totals).
  const isViewDataPending =
    isLoading || isCountsLoading || (counts === undefined && !hasCountsError);
  useReportClassContentReady(!isViewDataPending);

  // LMS grade sync: real per-class control when the class has an LTI deployment; a disabled
  // cross-class explainer in All Classwork when any class does. Both hooks run unconditionally — the
  // off-scope one is inert.
  const classLti = useClassLtiSync(classroomId);
  const crossLti = useAnyClassLtiDeployment(!classroomId);

  const renderLmsCell = (assignment: TeacherAssignmentRowFieldsFragment): React.ReactNode => {
    // Drafts have no grades to sync.
    if (isDraftSegment) {
      return null;
    }
    if (classroomId) {
      // Wait for the sync-status query so initialSynced is correct on the cell's first render
      // (useLtiSync seeds its state once and won't pick up a later prop change).
      return classLti.isReady && classLti.schoolHasLtiDeployment ? (
        <AssignmentLtiSyncCell
          assignmentId={assignment.id}
          classroomId={classroomId}
          schoolId={classLti.schoolId}
          ltiPlatformName={classLti.ltiPlatformName}
          classroomConnectedToLti={classLti.classroomConnectedToLti}
          initialSynced={classLti.isSynced(assignment.id)}
        />
      ) : null;
    }
    return crossLti.anyClassHasLtiDeployment ? <CrossClassLtiSyncCell /> : null;
  };

  // Precompute a stable cell element per row
  const lmsCellById = useMemo(() => {
    const cells = new Map<string, React.ReactNode>();
    assignments.forEach((assignment) => {
      cells.set(assignment.id, renderLmsCell(assignment));
    });
    return cells;
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [
    assignments,
    isDraftSegment,
    classroomId,
    classLti.isReady,
    classLti.schoolHasLtiDeployment,
    classLti.schoolId,
    classLti.ltiPlatformName,
    classLti.classroomConnectedToLti,
    // Stable per-syncedIds, so a sync-status refetch recomputes cells with current data.
    classLti.isSynced,
    crossLti.anyClassHasLtiDeployment
  ]);

  if (hasError) {
    return (
      <div className='pt-x4'>
        <ErrorRetryState
          copy='We couldn’t load your assignments.'
          subtitle='Something went wrong. Check your connection and try again.'
          onRetry={refreshListAndCounts}
        />
      </div>
    );
  }

  // First paint: one skeleton for the whole view, revealed in lockstep with the header.
  if (!revealed) {
    return <AssignmentsViewSkeleton />;
  }

  // Onboarding (whole scope empty)
  if (isScopeEmpty) {
    return <AssignmentsOnboardingEmptyState classroomId={classroomId} />;
  }

  return (
    <div className='gap-x3 pt-x4 flex flex-col'>
      <Heading as='h2' className='m-x0' size='s'>
        Assignments
      </Heading>
      <div className='gap-x2 flex flex-wrap items-center justify-between'>
        <SegmentedControl
          buttonClassName='font-normal'
          onSelect={handleSegmentChange}
          options={segmentOptions}
          selectedIdx={STATUS_SEGMENTS.findIndex((s) => s.value === segment)}
        />
        <div className='gap-x1 flex items-center'>
          {/* Drafts have no applicable filters (no status/creator yet) — hide the Filters menu. */}
          {!isDraftSegment && (
            <FiltersMenu
              createdBy={createdBy}
              assignmentType={assignmentType}
              dueDateStart={dueDateStart}
              dueDateEnd={dueDateEnd}
              onChange={setCreatedBy}
              onAssignmentTypeChange={setAssignmentType}
              onDueDateChange={(bound, value) =>
                bound === 'start' ? setDueDateStart(value) : setDueDateEnd(value)
              }
            />
          )}
          <SortDropdown
            options={SORT_OPTIONS}
            triggerIcon={['far', 'arrow-down-short-wide']}
            triggerLabel='Sort'
            value={sort}
            onChange={setSort}
          />
          {isSearchOpen ? (
            // React's onBlur bubbles like focusout: collapse when focus leaves with no query.
            <div
              onBlur={(e) => {
                if (!searchTerm && !e.currentTarget.contains(e.relatedTarget as Node | null)) {
                  setIsSearchOpen(false);
                }
              }}
            >
              <SearchInput
                autoFocus
                className='w-[256px]'
                placeholder='Search'
                rectangle
                value={searchTerm}
                onChange={(_e, value) => {
                  setSearchTerm(value);
                  debouncedSetSearch(value);
                }}
                onReset={() => {
                  clearSearch();
                  setIsSearchOpen(false);
                }}
              />
            </div>
          ) : (
            <IconButton
              color='secondary'
              icon='magnifying-glass'
              label='Search assignments'
              roundness='square'
              variant='outlined'
              onClick={() => setIsSearchOpen(true)}
            />
          )}
          <Button onClick={() => history.pushState(null, getCreateAssignmentPath(classroomId))}>
            <Icon className='mr-x1' icon='file-plus' iconStyle='regular' />
            New
          </Button>
        </div>
      </div>
      <div aria-live='polite' className='sr-only'>
        {isLoading || isCheckingScope
          ? 'Loading assignments…'
          : `Showing ${assignments.length} ${
              assignments.length === 1 ? 'assignment' : 'assignments'
            }`}
      </div>
      {assignments.length > 0 ? (
        <>
          <ul className='m-x0 p-x0 flex list-none flex-col'>
            {assignments.map((assignment) => (
              <li key={assignment.id}>
                <AssignmentRow
                  actorTeacherId={actorTeacherId}
                  assignment={assignment}
                  classroomId={classroomId}
                  lmsCell={lmsCellById.get(assignment.id)}
                  status={segment}
                  onDeleteComplete={refreshListAndCounts}
                />
              </li>
            ))}
          </ul>
          {hasNextPage && (
            <div className='py-x3 flex justify-center'>
              <Button disabled={isLoadingMore} onClick={loadMore}>
                Show more
                {isLoadingMore && <Icon className='ml-x1' icon='spinner' spin />}
              </Button>
            </div>
          )}
        </>
      ) : isCheckingScope || isLoading ? (
        <AssignmentRowsSkeleton />
      ) : hasFiltersOn ? (
        <AssignmentsFilteredEmptyState />
      ) : (
        <AssignmentStatusEmptyState segment={segment} classroomId={classroomId} />
      )}
    </div>
  );
};
